import { onboardingApi, userApi } from '../api';
import { useCurrentCommunityId, useCurrentUser } from '../redux/selector';
import { COMMUNITIES_KEY, LIST, profileQueryKey } from '../api/query';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { JoinCommunityRequest, Member, ClaimInvitationRequest } from '@quick/client';

const SUMMARY_KEY = 'SUMMARY';
const FULL_KEY = 'FULL';

const SHORT_STALE_TIME = 1000 * 5;

const communitySummaryKeys = {
  all: () => [COMMUNITIES_KEY, SUMMARY_KEY],
  lists: () => [...communitySummaryKeys.all(), LIST],
  list: (search: string = '') => [...communitySummaryKeys.lists(), search],
};

const communityKeys = {
  all: () => [COMMUNITIES_KEY, FULL_KEY],
  lists: () => [...communityKeys.all(), LIST],
  list: (userId?: number) => [...communityKeys.lists(), userId],
};

const DEFAULT_JOIN_COMMUNITIES_LIST_SIZE = 6;
export const useCommunitySummariesQuery = (search: string, size?: number) => {
  return useQuery(
    communitySummaryKeys.list(search),
    () => onboardingApi.listCommunities1({ search, size: size ?? DEFAULT_JOIN_COMMUNITIES_LIST_SIZE }),
    {
      keepPreviousData: true,
    }
  );
};

export const useJoinedCommunitiesQuery = () => {
  const user = useCurrentUser();
  return useQuery(
    communityKeys.list(user?.userId),
    async () => {
      if (user?.userId) {
        return userApi.listCommunities();
      }
      return [];
    },
    {
      staleTime: SHORT_STALE_TIME,
      keepPreviousData: true,
    }
  );
};

export const useCurrentCommunity = () => {
  const joinedCommunities = useJoinedCommunitiesQuery();
  const currentCommunityId = useCurrentCommunityId();
  return joinedCommunities.data?.find((it) => it.communityId === currentCommunityId);
};

export const useCurrentCommunityConfig = () => {
  return useCurrentCommunity()?.config;
};

export const useCommunityMutation = () => {
  const queryClient = useQueryClient();

  const joinCommunityMutation = useMutation((request: JoinCommunityRequest) => onboardingApi.joinCommunity(request), {
    onSuccess: async (response: Member) => {
      queryClient.invalidateQueries(communitySummaryKeys.lists());
      queryClient.invalidateQueries(communityKeys.lists());
      queryClient.invalidateQueries(profileQueryKey());
    },
  });

  const claimInvitationMutation = useMutation(
    (request: ClaimInvitationRequest) => onboardingApi.claimInvitation(request),
    {
      onSuccess: async (response: Member) => {
        queryClient.invalidateQueries(communityKeys.lists());
        queryClient.invalidateQueries(profileQueryKey());
      },
    }
  );

  return {
    joinCommunityMutation,
    claimInvitationMutation,
  };
};
